import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toast } from 'sonner';
import { Mail, Send, BarChart3 } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AuditChip } from '@/components/audit/AuditChip';
import { useApiError } from '@/lib/useApiError';
import { useInviteWaitlistEntry, useWaitlist } from '@/queries/phase2';
import {
  WAITLIST_STATES,
  type WaitlistEntry,
  type WaitlistState,
} from '@/schemas/admin/phase2/waitlist';

const searchSchema = z.object({
  state: z
    .enum(WAITLIST_STATES)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_authed/phase2/waitlist/')({
  validateSearch: searchSchema,
  component: WaitlistScreen,
});

function WaitlistScreen() {
  const { t } = useTranslation('phase2');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const list = useWaitlist({ ...(search.state ? { state: search.state } : {}) });
  const invite = useInviteWaitlistEntry();
  const resolveError = useApiError();

  function setStateFilter(next: WaitlistState | undefined) {
    void navigate({
      to: '/phase2/waitlist',
      search: () => (next ? { state: next } : {}),
      replace: true,
    });
  }

  async function inviteRow(row: WaitlistEntry) {
    try {
      await invite.mutateAsync(row.id);
      toast.success(
        t('waitlist.invitedTitle', { defaultValue: 'Invite sent.' }),
        {
          description: t('waitlist.invitedBody', {
            defaultValue: 'Position {position}.',
            position: row.position,
          }),
        }
      );
    } catch (e) {
      const resolved = resolveError(e);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const columns: readonly DataColumn<WaitlistEntry>[] = [
    {
      id: 'position',
      header: t('waitlist.col.position', { defaultValue: '#' }),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.position}
        </span>
      ),
      sortBy: (r) => r.position,
      align: 'end',
    },
    {
      id: 'email',
      header: t('waitlist.col.email', { defaultValue: 'Email' }),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-sm">
          {r.email_masked}
        </span>
      ),
    },
    {
      id: 'locale',
      header: t('waitlist.col.locale', { defaultValue: 'Locale' }),
      cell: (r) =>
        r.locale ? (
          <Badge tone="neutral">
            <span dir="ltr">{r.locale}</span>
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'where',
      header: t('waitlist.col.where', { defaultValue: 'Where' }),
      cell: (r) => (
        <span className="text-sm">
          {[r.city, r.country].filter(Boolean).join(', ') || '—'}
        </span>
      ),
    },
    {
      id: 'segments',
      header: t('waitlist.col.segments', { defaultValue: 'Segments' }),
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.segments ?? []).map((s) => (
            <Badge key={s} tone="neutral">
              {s}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'utm',
      header: t('waitlist.col.utm', { defaultValue: 'Source' }),
      hiddenInitially: true,
      cell: (r) => (
        <span dir="ltr" className="text-xs text-[color:var(--color-fg-muted)]">
          {r.source_utm?.utm_source ?? '—'}
        </span>
      ),
    },
    {
      id: 'state',
      header: t('waitlist.col.state', { defaultValue: 'State' }),
      cell: (r) => (
        <div className="flex items-center gap-1.5">
          <Badge
            tone={
              r.state === 'verified'
                ? 'success'
                : r.state === 'invited'
                  ? 'accent'
                  : r.state === 'converted'
                    ? 'info'
                    : r.state === 'unsubscribed'
                      ? 'danger'
                      : 'neutral'
            }
          >
            {t(`waitlist.states.${r.state}`, { defaultValue: r.state })}
          </Badge>
          {r.founding_member ? (
            <Badge tone="warn">
              {t('waitlist.foundingBadge', { defaultValue: 'Founding' })}
            </Badge>
          ) : null}
        </div>
      ),
      sortBy: (r) => r.state,
    },
    {
      id: 'verified',
      header: t('waitlist.col.verified', { defaultValue: 'Verified' }),
      cell: (r) => <DateTime value={r.email_verified_at} />,
    },
    {
      id: 'created',
      header: t('waitlist.col.created', { defaultValue: 'Joined' }),
      cell: (r) => <DateTime value={r.created_at} />,
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('waitlist.col.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => {
        const canInvite = r.state === 'pending' || r.state === 'verified';
        return (
          <div className="flex items-center justify-end gap-2">
            <AuditChip />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void inviteRow(r)}
              disabled={!canInvite || invite.isPending}
              title={
                canInvite
                  ? undefined
                  : t('waitlist.inviteDisabledHint', {
                      defaultValue: 'Only pending or verified entries can be invited.',
                    })
              }
            >
              <Send className="size-4" aria-hidden />
              <span className="sr-only md:not-sr-only md:text-xs">
                {t('waitlist.invite', { defaultValue: 'Invite' })}
              </span>
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('waitlist.title', { defaultValue: 'Waitlist' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('waitlist.subtitle', {
              defaultValue:
                'Showing the 500 most-recent entries by position. Emails are masked; reach out via support tooling for verified contact.',
            })}
          </p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/phase2/waitlist/metrics">
            <BarChart3 className="size-4" aria-hidden />
            {t('waitlist.openMetrics', { defaultValue: 'Open metrics' })}
          </Link>
        </Button>
      </header>

      <div className="flex flex-wrap gap-2">
        <FilterChip
          label={t('waitlist.filter.all', { defaultValue: 'All' })}
          active={!search.state}
          onClick={() => setStateFilter(undefined)}
        />
        {WAITLIST_STATES.map((s) => (
          <FilterChip
            key={s}
            label={t(`waitlist.states.${s}`, { defaultValue: s })}
            active={search.state === s}
            onClick={() => setStateFilter(s)}
          />
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<WaitlistEntry>
            data={list.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{
              title: t('waitlist.empty', { defaultValue: 'No waitlist entries match.' }),
              body: t('waitlist.emptyBody', {
                defaultValue: 'Try clearing the state filter to see everyone.',
              }),
            }}
            initialSort={{ id: 'position', direction: 'asc' }}
            caption={
              <span className="inline-flex items-center gap-2">
                <Mail className="size-4" aria-hidden />
                {t('waitlist.caption', {
                  defaultValue: 'Showing the 500 most-recent entries.',
                })}
              </span>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-[var(--radius-pill)] border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
          : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-fg)] hover:bg-[color:var(--color-surface-2)]'
      }`}
    >
      {label}
    </button>
  );
}
